import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

interface Coupon {
    _id: string;
    code: string;
    quantity: number;
    usedQuantity: number;
    maxSteps: number;
    discountPercent: number;
    maxUsesPerAccount: number;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
}

interface CouponDraft {
    code: string;
    quantity: string;
    maxSteps: string;
    discountPercent: string;
    maxUsesPerAccount: string;
    startsAt: string;
    endsAt: string;
}

const toDateTimeInput = (date: Date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
};

const createEmptyDraft = (): CouponDraft => {
    const startsAt = new Date();
    const endsAt = new Date(startsAt);
    endsAt.setDate(endsAt.getDate() + 7);
    return {
        code: '',
        quantity: '',
        maxSteps: '',
        discountPercent: '',
        maxUsesPerAccount: '1',
        startsAt: toDateTimeInput(startsAt),
        endsAt: toDateTimeInput(endsAt),
    };
};

const AdminCouponsPage: React.FC = () => {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [draft, setDraft] = useState<CouponDraft>(createEmptyDraft);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const loadCoupons = async () => {
        try {
            setCoupons(await apiService.get<Coupon[]>('/orders/coupons') || []);
            setError('');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không tải được danh sách mã giảm giá.');
        }
    };

    useEffect(() => {
        void loadCoupons();
    }, []);

    const updateDraft = (key: keyof CouponDraft, value: string) => {
        setDraft((current) => ({ ...current, [key]: value }));
    };

    const resetDraft = () => {
        setEditingId(null);
        setDraft(createEmptyDraft());
    };

    const editCoupon = (coupon: Coupon) => {
        setEditingId(coupon._id);
        setDraft({
            code: coupon.code,
            quantity: String(coupon.quantity),
            maxSteps: String(coupon.maxSteps),
            discountPercent: String(coupon.discountPercent),
            maxUsesPerAccount: String(coupon.maxUsesPerAccount),
            startsAt: toDateTimeInput(new Date(coupon.startsAt)),
            endsAt: toDateTimeInput(new Date(coupon.endsAt)),
        });
        setError('');
        setMessage('');
    };

    const saveCoupon = async () => {
        const quantity = Number(draft.quantity);
        const maxSteps = Number(draft.maxSteps);
        const discountPercent = Number(draft.discountPercent);
        const maxUsesPerAccount = Number(draft.maxUsesPerAccount);
        if (!draft.code.trim() || !draft.startsAt || !draft.endsAt) {
            setError('Vui lòng nhập mã và thời gian áp dụng.');
            return;
        }
        if (!Number.isInteger(quantity) || quantity < 1 || !Number.isInteger(maxSteps) || maxSteps < 1) {
            setError('Số lượng mã và số bước tối đa phải là số nguyên dương.');
            return;
        }
        if (!Number.isFinite(discountPercent) || discountPercent <= 0 || discountPercent > 100) {
            setError('Phần trăm giảm phải lớn hơn 0 và không vượt quá 100.');
            return;
        }
        if (!Number.isInteger(maxUsesPerAccount) || maxUsesPerAccount < 1) {
            setError('Số lượt dùng mỗi tài khoản phải là số nguyên dương.');
            return;
        }

        setIsSaving(true);
        setError('');
        try {
            const payload = {
                code: draft.code.trim().toUpperCase(),
                quantity,
                maxSteps,
                discountPercent,
                maxUsesPerAccount,
                startsAt: new Date(draft.startsAt).toISOString(),
                endsAt: new Date(draft.endsAt).toISOString(),
            };
            if (editingId) {
                await apiService.patch(`/orders/coupons/${editingId}`, payload);
                setMessage(`Đã cập nhật mã ${payload.code}.`);
            } else {
                await apiService.post('/orders/coupons', payload);
                setMessage(`Đã tạo mã ${payload.code}.`);
            }
            resetDraft();
            await loadCoupons();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được mã giảm giá.');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleCoupon = async (coupon: Coupon) => {
        try {
            await apiService.patch(`/orders/coupons/${coupon._id}`, { isActive: !coupon.isActive });
            await loadCoupons();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không cập nhật được trạng thái mã.');
        }
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-5xl space-y-5">
                <header>
                    <p className="text-sm font-medium text-blue-600">Cấu hình đơn hàng</p>
                    <h2 className="mt-1 text-2xl font-semibold text-gray-900">Quản lý mã giảm giá</h2>
                </header>
                {(message || error) && (
                    <div role={error ? 'alert' : 'status'} className={`rounded-lg px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {error || message}
                    </div>
                )}
                <section className="rounded-xl bg-white p-5 shadow-sm">
                    <h3 className="font-semibold">{editingId ? `Sửa mã ${draft.code}` : 'Tạo mã mới'}</h3>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <label className="text-sm text-gray-600">Mã giảm giá
                            <input value={draft.code} onChange={(event) => updateDraft('code', event.target.value.toUpperCase())} maxLength={32} placeholder="VD: SALE2026" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 uppercase text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Số lượng mã
                            <input value={draft.quantity} onChange={(event) => updateDraft('quantity', event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="100" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Số bước tối đa / đơn
                            <input value={draft.maxSteps} onChange={(event) => updateDraft('maxSteps', event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="10" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Giảm (%)
                            <input value={draft.discountPercent} onChange={(event) => updateDraft('discountPercent', event.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="15" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Lượt dùng / tài khoản
                            <input value={draft.maxUsesPerAccount} onChange={(event) => updateDraft('maxUsesPerAccount', event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="1" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Bắt đầu
                            <input type="datetime-local" value={draft.startsAt} onChange={(event) => updateDraft('startsAt', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                        <label className="text-sm text-gray-600">Kết thúc
                            <input type="datetime-local" value={draft.endsAt} onChange={(event) => updateDraft('endsAt', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900" />
                        </label>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button type="button" onClick={() => void saveCoupon()} disabled={isSaving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                            {isSaving ? 'Đang lưu...' : editingId ? 'Lưu thay đổi' : 'Tạo mã'}
                        </button>
                        {editingId && <button type="button" onClick={resetDraft} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Hủy sửa</button>}
                    </div>
                </section>
                <section className="overflow-hidden rounded-xl bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[850px] text-left text-sm">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                <tr><th className="px-4 py-3">Mã</th><th className="px-4 py-3">Đã dùng</th><th className="px-4 py-3">Giảm</th><th className="px-4 py-3">Giới hạn</th><th className="px-4 py-3">Thời gian</th><th className="px-4 py-3">Trạng thái</th><th className="px-4 py-3" /></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {coupons.map((coupon) => (
                                    <tr key={coupon._id}>
                                        <td className="px-4 py-3 font-semibold text-gray-900">{coupon.code}</td>
                                        <td className="px-4 py-3">{coupon.usedQuantity} / {coupon.quantity}</td>
                                        <td className="px-4 py-3">{coupon.discountPercent}%</td>
                                        <td className="px-4 py-3">Tối đa {coupon.maxSteps} bước · {coupon.maxUsesPerAccount} lượt / tài khoản</td>
                                        <td className="px-4 py-3 text-xs text-gray-600">{new Date(coupon.startsAt).toLocaleString('vi-VN')}<br />{new Date(coupon.endsAt).toLocaleString('vi-VN')}</td>
                                        <td className="px-4 py-3"><span className={coupon.isActive ? 'text-green-700' : 'text-gray-500'}>{coupon.isActive ? 'Đang bật' : 'Đã tắt'}</span></td>
                                        <td className="px-4 py-3"><div className="flex gap-2"><button type="button" onClick={() => editCoupon(coupon)} className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50">Sửa</button><button type="button" onClick={() => void toggleCoupon(coupon)} className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50">{coupon.isActive ? 'Tắt' : 'Bật'}</button></div></td>
                                    </tr>
                                ))}
                                {coupons.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Chưa có mã giảm giá.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminCouponsPage;